import Slider from "react-slick";
import {
   Icon_ListSummariesLeftArrow,
   Icon_ListSummariesRightArrow,
} from "../../BP2Components/Icons/icons";
import "./ListSummariesWidget.less";
import { useIntl } from "react-intl";
import projectConfig from "../../../projectConfig.json";
import { useState, useRef, useEffect, useMemo } from "react";
import Widget from "../../Widget/Widget";
import { getListV2 } from "../../../services/api/Api";
import SettingsModal from "../../Modal/SettingsModal";
import { ListSummariesModal } from "./ListSummariesSettings/ListSummariesModal";
import clsx from "clsx";
import { Button, Message } from "../../BP2Components";
import { Table } from "antd";
import { getLabelFromTechName } from "../../Newsletter/NewsletterSteps/ThirdStep";
import LogoImage from "../../../util/LogoImage";
import IntlMessages from "../../../util/IntlMessages";
import { useDispatch } from "react-redux";
import { setWidgetInSheetParams } from "../../../appRedux/actions/Boards";
import { Link } from "react-router-dom";
import { getListV3 } from "../../../services/restAPI/ListsV2";
import { getV3DataUrl } from "../../../services/restAPI/FormsV3";
import { SummaryTable } from "./types";
import { buildExternalSummaries } from "./utils";

const SLIDES_TO_SHOW = 3;

export const ListSummariesWidget = ({ value, data, role }) => {
   const [benefitIndex, setBenefitIndex] = useState<number>(0);
   const [areDetailsOpen, setAreDetailsOpen] = useState<boolean>(true);
   const intl = useIntl();
   const sliderRef = useRef<Slider | null>(null);
   const widgetRef = useRef<HTMLDivElement | null>(null);
   const [pocSums, setPocSums] = useState<Record<string, number>>({});

   const { columnSummaries, listId, name, id, titleKey } = value;
   const isV3 = listId?.length > 10;

   const [summaries, setSummaries] = useState(columnSummaries || []);
   const dispatch = useDispatch();

   const [tableData, setTableData] = useState<SummaryTable>({
      cols: [],
      rows: [],
   });

   const colsWithSum = useMemo(() => {
      if (!tableData.cols.length) return [];

      return isV3
         ? tableData.cols.filter(
              (col) =>
                 col?.options &&
                 "sumAllValues" in col.options &&
                 col.options.sumAllValues !== false,
           )
         : tableData.cols.filter(
              (col) =>
                 col?.options &&
                 "sumAllRecords" in col.options &&
                 col.options.sumAllRecords !== null,
           );
   }, [isV3, tableData.cols]);

   const optionsForBenefitSlides = useMemo(
      () =>
         colsWithSum.map((col) => {
            const value = isV3 ? col.meta?.hid : col.options.techName;
            if (!value) {
               throw new Error("Missing column identifier");
            }

            return {
               value,
               label: col.label,
            };
         }),
      [colsWithSum, isV3],
   );

   const sumsFromBenefits = useMemo(() => {
      if (isV3) return pocSums;

      return colsWithSum.reduce<Record<string, number>>((acc, col) => {
         acc[col.options.techName] = col.options.sumAllRecords || 0;
         return acc;
      }, {});
   }, [colsWithSum, isV3, pocSums]);

   const dataSource = useMemo(() => {
      if (!tableData.rows.length) return [];

      const sumKeys = colsWithSum.map((col) => col.label);
      const rowId = getLabelFromTechName("id", tableData.cols);

      return tableData.rows.map((row) =>
         sumKeys.reduce(
            (acc, key) => {
               if (row[key] !== undefined) {
                  acc[key] = row[key];
               }
               return acc;
            },
            { id: row[rowId] },
         ),
      );
   }, [tableData.rows, tableData.cols, colsWithSum]);

   const columnsForDetailsTable = useMemo(
      () =>
         colsWithSum.map((col) => ({
            title: col.label,
            dataIndex: col.label,
            key: col.options.techName,
            render: (text: unknown) => {
               const value = Number(text);
               return <span>{Number.isNaN(value) ? "-" : value.toFixed(2)}</span>;
            },
         })),
      [colsWithSum],
   );

   const logoOnSlide = projectConfig?.logo?.listSummary?.src;

   const slidesCount = summaries?.length ?? 0;
   const slidesToShowDefault = Math.min(SLIDES_TO_SHOW, slidesCount);

   const benefitsSliderSettings = useMemo(
      () => ({
         nextArrow: <Icon_ListSummariesRightArrow />,
         prevArrow: <Icon_ListSummariesLeftArrow />,
         infinite: slidesCount > 1,
         speed: 500,
         slidesToScroll: 1,
         swipe: true,
         dots: false,
         draggable: true,
         beforeChange: (_: number, newIndex: number) => {
            setBenefitIndex(newIndex);
         },
         slidesToShow: slidesToShowDefault,
         responsive: [
            {
               breakpoint: 1324,
               settings: {
                  slidesToShow: Math.min(2, slidesCount),
               },
            },
            {
               breakpoint: 1040,
               settings: {
                  slidesToShow: Math.min(1, slidesCount),
               },
            },
         ],
      }),
      [slidesCount, slidesToShowDefault],
   );

   useEffect(() => {
      const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

      const fetchV3SumColumns = async (sumColumnsUrl: string) => {
         while (true) {
            const sumColumnsRes = await getV3DataUrl(sumColumnsUrl);
            Message.loading(intl.formatMessage({ id: "success.sumColumnsDataLoaded" }));

            if (sumColumnsRes.data.status === "DONE") {
               setPocSums(sumColumnsRes.data.result);
               Message.destroy();
               return;
            }

            if (sumColumnsRes.data.status === "ERROR") {
               throw new Error(sumColumnsRes.data.error);
            }

            await sleep(1500);
            Message.destroy();
         }
      };

      const fetchListV3 = async () => {
         const res = await getListV3(listId, 1, 1000);
         if (res.status !== 200) {
            throw new Error("Failed to load list data");
         }

         const sumColumnsUrl = res.data.meta?.sumColumnsUrl;

         setTableData({
            cols: res.data.columns,
            rows: res.data.data,
         });

         if (sumColumnsUrl) {
            await fetchV3SumColumns(sumColumnsUrl);
         }
      };

      const fetchListV2 = async () => {
         const res = await getListV2(listId, 1, 1000);
         if (res.status !== 200) {
            throw new Error("Failed to load list data");
         }

         setTableData({
            cols: res.data.default.columns,
            rows: res.data.default.data,
         });
      };

      const handleListRequest = async () => {
         try {
            if (!listId) return;

            if (value?.mode === "external") {
               const res = isV3
                  ? await getListV3(listId, 1, 1000)
                  : await getListV2(listId, 1, 1000);
               const payload = res.data?.default ?? res.data;
               if (res.status !== 200 || !Array.isArray(payload?.data)) {
                  Message.error(intl.formatMessage({ id: "error.loadingListData" }));
                  return;
               }

               const mapValuesToSummaries = buildExternalSummaries(
                  payload.data,
                  value.columnMapping ?? {},
               );
               setSummaries(mapValuesToSummaries);
               dispatch(
                  setWidgetInSheetParams({ ...value, columnSummaries: mapValuesToSummaries }),
               );
            } else if (isV3) {
               await fetchListV3();
            } else {
               await fetchListV2();
            }
         } catch (error) {
            Message.error(intl.formatMessage({ id: "error.loadingListData" }));
            console.error("Failed to load list data:", error);
         }
      };

      handleListRequest();
   }, [listId, isV3, value?.mode]);

   useEffect(() => {
      setSummaries(value.columnSummaries || []);
   }, [value.columnSummaries]);

   return (
      <div ref={widgetRef}>
         <Widget
            role={role}
            extra={
               (role === "OWNER" || role === "MODERATOR") && (
                  //@ts-ignore
                  <SettingsModal widgetName={name} widgetId={id} icon="setting" deleteIcon="true">
                     <ListSummariesModal
                        value={value}
                        optionsForBenefitSlides={optionsForBenefitSlides}
                        sumsFromBenefits={sumsFromBenefits}
                        data={data}
                     />
                  </SettingsModal>
               )
            }
            bgColor="transparent"
            className="list-summaries__widget"
         >
            <div
               className={clsx("list-summaries", {
                  "list-summaries--no-margin": SLIDES_TO_SHOW >= summaries?.length,
               })}
            >
               <h1 className="list-summaries__title">{titleKey || ""}</h1>

               <div className="list-summaries__slider-wrapper">
                  {summaries?.length > 0 && (
                     <Slider ref={sliderRef} {...benefitsSliderSettings}>
                        {summaries.map((column) => {
                           const {
                              columnKey,
                              titleTranslationKey,
                              valuePrefix,
                              valueSuffix,
                              valueLimit,
                              valueLimitDescriptionTranslationKey,
                              valueDescriptionTranslationKey,
                              id,
                              indicatorValue,
                              indicatorCompletionPercent,
                              indicatorCompletionSuffix,
                              linkToBoard,
                           } = column;

                           const percentage =
                              value.mode === "external"
                                 ? indicatorCompletionPercent
                                 : valueLimit > 0
                                   ? (sumsFromBenefits[columnKey] / valueLimit) * 100
                                   : 0;

                           const actualValue =
                              value.mode === "external"
                                 ? indicatorValue
                                 : sumsFromBenefits[columnKey];

                           const completionSuffix =
                              value.mode === "external" ? indicatorCompletionSuffix : valueSuffix;

                           const Wrapper: React.ElementType =
                              value.mode === "external" && linkToBoard ? Link : "div";

                           const wrapperProps =
                              value.mode === "external" && linkToBoard
                                 ? {
                                      to: linkToBoard,
                                   }
                                 : {};

                           return (
                              <Wrapper className="list-summaries__item" key={id} {...wrapperProps}>
                                 <h2 className="list-summaries__item-title">
                                    <IntlMessages id={titleTranslationKey} />
                                 </h2>

                                 <p className="list-summaries__item-actual-benefit">
                                    <span className="list-summaries__item-max-benefit-title">
                                       {valuePrefix && valuePrefix}
                                    </span>
                                    {actualValue} {valueSuffix && valueSuffix}
                                    <span className="list-summaries__item-max-benefit-title">
                                       {valueDescriptionTranslationKey && (
                                          <IntlMessages id={valueDescriptionTranslationKey} />
                                       )}
                                    </span>
                                 </p>

                                 <div className="list-summaries__item-progress-container">
                                    <div
                                       className="list-summaries__item-progress-bar"
                                       style={{ width: `${percentage}%` }}
                                    />
                                 </div>

                                 <div className="list-summaries__item-max-benefit">
                                    <p className="list-summaries__item-max-benefit-title">
                                       <IntlMessages id={valueLimitDescriptionTranslationKey} />
                                    </p>
                                    <p className="list-summaries__item-max-benefit-value">
                                       {valueLimit} {completionSuffix}
                                    </p>
                                 </div>

                                 {logoOnSlide && (
                                    <div className="list-summaries__background-image">
                                       <LogoImage
                                          src={projectConfig?.logo?.listSummary?.src}
                                          alt={projectConfig?.logo?.listSummary?.alt}
                                          className="list-summaries__logo"
                                       />
                                    </div>
                                 )}
                              </Wrapper>
                           );
                        })}
                     </Slider>
                  )}
               </div>

               <div className="list-summaries__footer">
                  {SLIDES_TO_SHOW < summaries.length && (
                     <div className="list-summaries__dots">
                        {Array.from({ length: summaries.length }).map((_, index) => (
                           <span
                              key={index}
                              className={clsx("list-summaries__dot", {
                                 "list-summaries__dot--active": index === benefitIndex,
                              })}
                              onClick={() => {
                                 setBenefitIndex(index);
                                 sliderRef.current?.slickGoTo(index);
                              }}
                           />
                        ))}
                     </div>
                  )}

                  {value?.mode !== "external" && (
                     <Button
                        buttonType="primary"
                        className="list-summaries__show-more"
                        onClick={() => setAreDetailsOpen((prev) => !prev)}
                     >
                        {areDetailsOpen
                           ? intl.formatMessage({ id: "summaries.showLessBenefits" })
                           : intl.formatMessage({ id: "summaries.showMoreBenefits" })}
                     </Button>
                  )}
               </div>

               {areDetailsOpen && value?.mode !== "external" && (
                  <Table
                     columns={columnsForDetailsTable}
                     dataSource={dataSource}
                     className="list-summaries__details-table"
                  />
               )}
            </div>
         </Widget>
      </div>
   );
};
